import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { formatDuration, parseServerDate } from '@/lib/dates';

/**
 * Live-work "agent is running" panel — generic over agent kind. Each agent
 * passes its own workflow-step labels + per-step duration estimate +
 * headline/subcopy. Mirrors legacy `.status-message.processing` +
 * `.workflow-stepper` in agent-progress.css.
 *
 * The current step is estimated from elapsed-time / stepSeconds — agents
 * don't report mid-run progress, so the stepper is a time-based
 * approximation that lands on the last step when the run finishes.
 */
export function AgentPollingPanel({
  startedAt,
  workflowSteps,
  stepSeconds,
  title,
  subtitle,
}: {
  startedAt: string | null;
  workflowSteps: readonly string[];
  stepSeconds: number;
  title: ReactNode;
  subtitle: string;
}) {
  const current = useCurrentStep(startedAt, workflowSteps.length, stepSeconds);
  const elapsed = useElapsedLabel(startedAt);
  const denom = Math.max(1, workflowSteps.length - 1);
  // Cap below 100% while the run is still going: a fully-filled rail reads as
  // "done", but the stepper is only a time estimate — the run isn't complete
  // until its status says so (the last step stays "in progress", never checked off).
  const progressPct = Math.min(0.92, current / denom);

  return (
    <div
      className="
        text-center
        py-[30px] px-8 pb-[26px] mb-[22px]
        rounded-[14px] border border-line
        bg-[linear-gradient(180deg,#fafbfd_0%,#ffffff_100%)]
        shadow-[0_2px_10px_rgba(20,40,70,0.04)]
        text-ink
      "
    >
      <h3 className="font-serif text-2xl font-semibold text-navy -tracking-[0.015em] leading-[1.15] m-0 mb-2">
        {title}
      </h3>
      <p className="font-sans text-base text-muted max-w-[560px] mx-auto leading-[1.6] m-0">
        {subtitle}
      </p>
      {/* HONEST time signal: real elapsed time, never a predicted duration
          (run times vary too much to promise). */}
      {elapsed && (
        <p className="font-sans text-[13px] text-muted/80 m-0 mt-2 [font-variant-numeric:tabular-nums]">
          Elapsed: {elapsed}
        </p>
      )}

      <Spinner />

      <WorkflowStepper
        steps={workflowSteps}
        current={current}
        progressPct={progressPct}
      />
    </div>
  );
}

function Spinner() {
  return (
    <div
      aria-hidden="true"
      className="
        inline-block size-7 rounded-full
        border-[2.5px] border-line border-t-electric
        animate-spin
        mt-[18px] mb-1 mx-auto
      "
    />
  );
}

function WorkflowStepper({
  steps,
  current,
  progressPct,
}: {
  steps: readonly string[];
  current: number;
  progressPct: number;
}) {
  return (
    <div
      role="group"
      aria-label="Analysis workflow"
      className="relative flex gap-1 mt-[22px] mb-1"
    >
      <StepperRail steps={steps} progressPct={progressPct} />
      {steps.map((label, i) => {
        const state = i < current ? 'completed' : i === current ? 'current' : 'upcoming';
        return <WorkflowStep key={i} num={i + 1} label={label} state={state} />;
      })}
    </div>
  );
}

function StepperRail({
  steps,
  progressPct,
}: {
  steps: readonly string[];
  progressPct: number;
}) {
  // Edge offset from step-pill center to container edge, expressed as a
  // fraction of container width. With N equal-width steps each pill is at
  // (i + 0.5) / N — the first pill center sits 0.5/N from the left edge,
  // so we shift the rail in by `1 / (2 * N)`. For 7 steps that's 1/14, for
  // 6 steps that's 1/12, etc.
  const edgeOffset = `calc(100% / ${steps.length * 2})`;
  return (
    <>
      <div
        aria-hidden="true"
        className="absolute top-[21px] h-0.5 bg-line rounded-sm z-0"
        style={{ left: edgeOffset, right: edgeOffset }}
      />
      <div
        aria-hidden="true"
        className="absolute top-[21px] h-0.5 bg-electric rounded-sm z-0 transition-[width] duration-500 ease-out"
        style={{
          left: edgeOffset,
          width: `calc((100% - 2 * ${edgeOffset}) * ${progressPct})`,
        }}
      />
    </>
  );
}

type StepState = 'completed' | 'current' | 'upcoming';

function WorkflowStep({
  num,
  label,
  state,
}: {
  num: number;
  label: string;
  state: StepState;
}) {
  return (
    <div className="relative z-10 flex-1 min-w-0 flex flex-col items-center text-center gap-2.5 py-1.5 px-1 bg-transparent">
      <div
        className={cn(
          'size-[30px] rounded-full border-[1.5px] flex items-center justify-center font-bold text-[12.5px] shrink-0 transition-colors',
          STEP_NUM_CLASSES[state],
        )}
        style={state === 'current' ? { animation: 'var(--animate-workflow-pulse)' } : undefined}
      >
        {num}
      </div>
      <div
        className={cn(
          'text-[11.5px] leading-[1.4] tracking-[0.2px] transition-colors',
          STEP_LABEL_CLASSES[state],
        )}
      >
        {label}
      </div>
    </div>
  );
}

const STEP_NUM_CLASSES: Record<StepState, string> = {
  upcoming: 'bg-white text-muted border-line-strong',
  completed: 'bg-electric text-white border-electric',
  current: 'bg-navy text-white border-navy',
};

const STEP_LABEL_CLASSES: Record<StepState, string> = {
  upcoming: 'text-muted font-semibold',
  completed: 'text-navy font-semibold',
  current: 'text-navy font-bold',
};

/** "m:ss" (or "h:mm:ss") since the run started — ticks every second. */
function useElapsedLabel(startedAt: string | null): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  if (!startedAt) return null;
  const started = parseServerDate(startedAt);
  if (!started) return null;
  const total = Math.max(0, Math.floor((now - started.getTime()) / 1000));
  return formatDuration(total);
}

function useCurrentStep(
  startedAt: string | null,
  stepCount: number,
  stepSeconds: number,
): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  if (!startedAt) return 0;
  const started = parseServerDate(startedAt);
  if (!started) return 0;
  const elapsedSec = Math.max(0, (now - started.getTime()) / 1000);
  const idx = Math.floor(elapsedSec / stepSeconds);
  return Math.min(idx, stepCount - 1);
}
